// TaskDetail — Single task view: "← All tasks" back link + task name + subtasks
// Renders subtasks dynamically via TaskExecution/SubtaskRenderer
// Consecutive checkbox-type subtasks are grouped under a "Checklist" heading
// Each group gets a "+ Add checklist item" button (wired in next phase)

import React, { useMemo } from "react";
import { Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { SPACING } from "#utils/spacing";
import { SubtaskRenderer } from "../TaskExecution";
import * as execStyles from "../TaskExecution/styles_vanilla.css";
import { SubtaskItemType, TaskStepStatus, TaskType } from "../types";
import { canViewerActAsAssignee } from "../utils/taskAssignment";
import { MAKER_ROLE_ACTIONS } from "./constants";
import * as styles from "./styles_vanilla.css";
import type { SubtaskItem } from "../types";
import type { TaskDetailProps } from "./types";

// Checkbox-family types that get grouped under "Checklist"
const CHECKLIST_TYPES = new Set<SubtaskItemType>([
  SubtaskItemType.CHECKBOX,
  SubtaskItemType.CHECKBOX_GROUP,
  SubtaskItemType.CHECKBOX_WITH_INPUT,
  SubtaskItemType.ATTESTATION,
]);

// Task statuses where subtasks should be rendered read-only.
// Typed as string so the comparison with `task.status` (BE-sourced string) is
// safe under @typescript-eslint/no-unsafe-enum-comparison.
const DISABLED_TASK_STATUSES = new Set<string>([
  TaskStepStatus.COMPLETED,
  TaskStepStatus.SKIPPED,
  TaskStepStatus.PENDING_REVIEW,
  TaskStepStatus.EXTERNAL_APPROVAL,
]);

type SubtaskGroup =
  | { type: "checklist"; items: SubtaskItem[] }
  | { type: "standalone"; item: SubtaskItem };

/** Group consecutive checkbox-type subtasks into checklist groups */
function groupSubtasks(subtasks: SubtaskItem[]): SubtaskGroup[] {
  const groups: SubtaskGroup[] = [];
  let currentChecklist: SubtaskItem[] = [];

  const flushChecklist = () => {
    if (currentChecklist.length > 0) {
      groups.push({ type: "checklist", items: [...currentChecklist] });
      currentChecklist = [];
    }
  };

  for (const subtask of subtasks) {
    if (CHECKLIST_TYPES.has(subtask.itemType)) {
      currentChecklist.push(subtask);
    } else {
      flushChecklist();
      groups.push({ type: "standalone", item: subtask });
    }
  }
  flushChecklist();

  return groups;
}

export const TaskDetail: React.FC<TaskDetailProps> = ({
  task,
  onBack,
  workflowUserId,
  actionLoading = false,
}) => {
  const isNotAssignee = !canViewerActAsAssignee(task, workflowUserId);
  const isDisabled = DISABLED_TASK_STATUSES.has(task.status) || isNotAssignee || actionLoading;

  // Upload access: on MAKER_CHECKER tasks only the maker may upload.
  // We derive the current user's role from availableActions (server-computed
  // per-user) rather than currentUserId, which is not reliably threaded.
  // If the user has a maker submission action they are the maker; everyone
  // else (checker, observer, unassigned viewer) gets upload hidden.
  const isMakerCheckerTask = task.taskType === TaskType.MAKER_CHECKER;
  const isMakerRole =
    isMakerCheckerTask && task.availableActions.some((a) => MAKER_ROLE_ACTIONS.has(a.action));
  const isUploadDisabled = isMakerCheckerTask ? !isMakerRole : isDisabled;

  const subtaskGroups = useMemo(() => groupSubtasks(task.subtasks), [task.subtasks]);
  const mirroredSubtasks = task.mirroredSubtasks ?? [];
  const hasMirroredSubtasks = mirroredSubtasks.length > 0;

  return (
    <Flex flexDirection="column" gap={SPACING.MEDIUM} className={styles.taskDetailContainer}>
      <div className={styles.taskDetailCard}>
        {/* Back link */}
        <button className={styles.backLink} onClick={onBack} data-testid="task-detail-back">
          <Icon type="arrow-left" size="small" color={Colors.Neutral600} />
          All tasks
        </button>

        <Text variant="h3" data-testid="task-detail-title" className={styles.taskDetailTitle}>
          {task.name}
        </Text>

        {hasMirroredSubtasks && (
          <div
            className={execStyles.checklistGroup}
            data-testid="task-detail-mirrored-subtasks"
          >
            {mirroredSubtasks.map((item) => (
              <SubtaskRenderer key={`mirror-${item.id}`} item={item} disabled />
            ))}
          </div>
        )}

        {subtaskGroups.length > 0 && (
          <>
            {subtaskGroups.map((group, idx) => {
              if (group.type === "checklist") {
                return (
                  <div key={`checklist-${idx}`} className={execStyles.checklistGroup}>
                    <span className={execStyles.checklistGroupLabel}>Checklist</span>
                    {group.items.map((item) => (
                      <SubtaskRenderer
                        key={item.id}
                        item={item}
                        disabled={isDisabled}
                        uploadDisabled={isUploadDisabled}
                      />
                    ))}
                  </div>
                );
              }
              return (
                <SubtaskRenderer
                  key={group.item.id}
                  item={group.item}
                  disabled={isDisabled}
                  uploadDisabled={isUploadDisabled}
                />
              );
            })}
          </>
        )}

        {subtaskGroups.length === 0 && !hasMirroredSubtasks && (
          <Text variant="regular-md" color={Colors.Neutral600}>
            No subtasks for this task
          </Text>
        )}
      </div>
    </Flex>
  );
};

export default TaskDetail;
